/**
 * @file InstaZ retinal imaging report generator (jsPDF).
 *
 * This is a port of the InstaZ desktop app's report generator
 * (afc-electron-test/renderer/src/utils/pdfGenerator.js) so that the web dashboard
 * produces a pixel-identical "Retinal Imaging Report - Remidio Insta Z" PDF.
 *
 * The desktop app pulls patient/image/site data from its local Electron DB and image
 * cache. On the dashboard that data already lives in the report flows, so this module
 * accepts everything as plain arguments. The only meaningful adaptation is image loading:
 * dashboard images are remote URLs, so each is fetched as a blob and turned into an
 * object URL before being drawn — this keeps the masking canvas same-origin and avoids
 * the cross-origin "tainted canvas" error that would otherwise break toDataURL().
 */
import jsPDF from "jspdf";
import qrcode from "qrcode-generator";
import remidioLogo from "../assets/images/remidio-logo-white-background.png";

/** Best-effort gender normalization to a single uppercase initial (M/F/...). */
function normalizeGender(gender, fallback = "N/A") {
  if (!gender) return fallback;
  const g = String(gender).trim();
  if (g.length === 0) return fallback;
  return g;
}

/**
 * Load a (possibly cross-origin) image URL into a same-origin object URL so it can be
 * drawn onto a canvas and read back via toDataURL without tainting. Falls back to the
 * original URL if the fetch fails.
 */
async function loadImageSrc(url) {
  if (!url) return null;
  if (typeof url === "string" && url.startsWith("data:")) return url;
  try {
    const response = await fetch(url);
    if (!response.ok) return url;
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  } catch (e) {
    return url;
  }
}

/**
 * Generate the InstaZ retinal imaging report PDF.
 *
 * @param {Object} options
 * @param {Object} options.patient - { name, patientId, age, dateOfBirth, gender }
 * @param {Array}  options.images  - [{ src, eye: 'Left'|'Right', isMontage: boolean, date: string }]
 * @param {string} options.layout  - '1x1' | '1x2' | '1x3' | '2x2'
 * @param {string} [options.qrCodeLink] - optional QR code link
 * @param {string} [options.siteLogoData] - base64 / data URL / URL of the site logo
 * @param {string} [options.signatureData] - data URL of the doctor's signature
 * @param {string} [options.orgName]
 * @param {string} [options.siteName]
 * @param {number} [options.maskSize=3] - circular fundus mask size (0-4)
 * @returns {Promise<Blob>} the report PDF as a Blob (ready for FormData upload / download)
 */
export async function generateInstaZReportPDF({
  patient,
  images,
  layout = "1x1",
  qrCodeLink = null,
  siteLogoData = null,
  signatureData = null,
  orgName = null,
  siteName = null,
  maskSize = 3
}) {
  const parsedMask =
    Number.isFinite(maskSize) && maskSize >= 0 && maskSize <= 4 ? maskSize : 3;

  // Parse layout (e.g., '1x2' -> rows: 1, cols: 2)
  const [rows, cols] = layout.split("x").map(Number);

  const selectedImages = (images || []).filter(img => img && img.src);
  if (selectedImages.length === 0) {
    throw new Error("No images selected");
  }

  // Separate regular images from montage images. Montage images always use 1x1 layout
  // and are appended at the end (not counted in the regular grid).
  const regularImages = selectedImages.filter(img => !img.isMontage);
  const montageImages = selectedImages.filter(img => img.isMontage);

  // Sort regular images: RE (Right) first, then LE (Left).
  regularImages.sort((a, b) => {
    const aIsRight = a.eye === "Right" || a.eye !== "Left";
    const bIsRight = b.eye === "Right" || b.eye !== "Left";
    if (aIsRight && !bIsRight) return -1;
    if (!aIsRight && bIsRight) return 1;
    return 0;
  });

  // PDF dimensions (A4 Landscape in mm: 297 x 210)
  const pageWidth = 297;
  const pageHeight = 210;
  const margin = 10;
  const contentWidth = pageWidth - margin * 2;
  const contentHeight = pageHeight - margin * 2;

  // Header area dimensions
  const topHeaderHeight = 12;
  const greenBarHeight = 5;
  const patientDataHeight = 10;
  const spacingAfterPatientData = 4;
  const headerHeight =
    topHeaderHeight +
    greenBarHeight +
    patientDataHeight +
    spacingAfterPatientData;
  const imageAreaHeight = contentHeight - headerHeight - 22; // 22 for footer/comment area
  const imageAreaWidth = contentWidth;

  // Calculate image dimensions based on layout for regular images (square cells).
  const imagesPerPage = rows * cols;
  const gapSize = 5;
  const maxCellWidth = (imageAreaWidth - (cols - 1) * gapSize) / cols;
  const maxCellHeight = (imageAreaHeight - (rows - 1) * gapSize) / rows;
  const cellSize = Math.min(maxCellWidth, maxCellHeight);
  const imageWidth = cellSize;
  const imageHeight = cellSize;
  const totalGridWidth = cols * cellSize + (cols - 1) * gapSize;
  const totalGridHeight = rows * cellSize + (rows - 1) * gapSize;
  const gridOffsetX = (imageAreaWidth - totalGridWidth) / 2;
  const gridOffsetY = (imageAreaHeight - totalGridHeight) / 2;

  // Montage images always use 1x1 layout (non-square, use full space)
  const montageImageWidth = imageAreaWidth;
  const montageImageHeight = imageAreaHeight;

  const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });

  const drawPageTemplate = async isFirstPage => {
    await drawTopHeader(
      pdf,
      margin,
      topHeaderHeight,
      contentWidth,
      siteLogoData,
      orgName,
      siteName
    );
    drawGreenHeading(
      pdf,
      margin,
      topHeaderHeight,
      greenBarHeight,
      contentWidth
    );
    drawPatientDataLine(
      pdf,
      patient,
      margin,
      topHeaderHeight + greenBarHeight + 2,
      patientDataHeight,
      contentWidth
    );
    const footerY = margin + headerHeight + imageAreaHeight + 5;
    await drawFooter(
      pdf,
      patient,
      margin,
      footerY,
      contentWidth,
      qrCodeLink,
      isFirstPage,
      signatureData
    );
  };

  let currentPageIndex = 0;
  const totalRegularPages =
    regularImages.length > 0
      ? Math.ceil(regularImages.length / imagesPerPage)
      : 0;
  const isFirstPage = () => currentPageIndex === 0;

  // Process regular images with the user-selected layout.
  for (let pageIndex = 0; pageIndex < totalRegularPages; pageIndex++) {
    if (currentPageIndex > 0) {
      pdf.addPage();
    }

    await drawPageTemplate(isFirstPage());

    const startIndex = pageIndex * imagesPerPage;
    const endIndex = Math.min(startIndex + imagesPerPage, regularImages.length);
    const pageImages = regularImages.slice(startIndex, endIndex);

    const imageStartY = margin + headerHeight + gridOffsetY;
    const imageStartX = margin + gridOffsetX;

    // Fill grid column-first (top to bottom, then left to right).
    let imageIndex = 0;
    for (let col = 0; col < cols; col++) {
      for (let row = 0; row < rows; row++) {
        if (imageIndex < pageImages.length) {
          const image = pageImages[imageIndex];
          const x = imageStartX + col * (imageWidth + gapSize);
          const y = imageStartY + row * (imageHeight + gapSize);

          const imageDataUrl = await loadImageSrc(image.src);

          if (!imageDataUrl) {
            pdf.setFillColor(255, 255, 255);
            pdf.circle(
              x + imageWidth / 2,
              y + imageHeight / 2,
              Math.min(imageWidth, imageHeight) / 2,
              "F"
            );
            imageIndex++;
            continue;
          }

          await drawImage(
            pdf,
            imageDataUrl,
            x,
            y,
            imageWidth,
            imageHeight,
            parsedMask
          );

          const eyeLabel =
            image.eye === "Left" ? "LE" : image.eye === "Right" ? "RE" : "RE";
          pdf.setFontSize(9);
          pdf.setTextColor(0, 0, 0);
          pdf.text(eyeLabel, x + 3, y + 5);

          const examDateStr = image.date || "";
          pdf.setFontSize(8);
          pdf.setTextColor(80, 80, 80);
          pdf.text(examDateStr, x + imageWidth - 3, y + imageHeight - 2, {
            align: "right"
          });

          imageIndex++;
        } else {
          break;
        }
      }
      if (imageIndex >= pageImages.length) {
        break;
      }
    }

    currentPageIndex++;
  }

  // Process montage images - always 1x1, one per page, appended at the end.
  for (
    let montageIndex = 0;
    montageIndex < montageImages.length;
    montageIndex++
  ) {
    if (currentPageIndex > 0) {
      pdf.addPage();
    }

    await drawPageTemplate(isFirstPage());

    const image = montageImages[montageIndex];
    const imageStartY = margin + headerHeight;

    const imageDataUrl = await loadImageSrc(image.src);

    if (!imageDataUrl) {
      pdf.setFillColor(255, 255, 255);
      pdf.rect(margin, imageStartY, montageImageWidth, montageImageHeight, "F");
      currentPageIndex++;
      continue;
    }

    await drawMontageImage(
      pdf,
      imageDataUrl,
      margin,
      imageStartY,
      montageImageWidth,
      montageImageHeight
    );

    const eyeLabel =
      image.eye === "Left" ? "LE" : image.eye === "Right" ? "RE" : "RE";
    pdf.setFontSize(9);
    pdf.setTextColor(0, 0, 0);
    pdf.text(eyeLabel, margin + 3, imageStartY + 5);

    const examDateStr = image.date || "";
    pdf.setFontSize(8);
    pdf.setTextColor(80, 80, 80);
    pdf.text(
      examDateStr,
      margin + montageImageWidth - 3,
      imageStartY + montageImageHeight - 2,
      { align: "right" }
    );

    currentPageIndex++;
  }

  return pdf.output("blob");
}

async function drawTopHeader(
  pdf,
  margin,
  height,
  contentWidth,
  siteLogoData = null,
  orgName = null,
  siteName = null
) {
  const y = margin;

  const logoWidth = 30;
  const logoHeight = 8;
  const logoX = margin + 5;
  const logoY = y + 3;

  if (siteLogoData) {
    try {
      let logoDataUrl = siteLogoData;
      if (
        typeof siteLogoData === "string" &&
        siteLogoData.startsWith("data:")
      ) {
        logoDataUrl = siteLogoData;
      } else if (
        typeof siteLogoData === "string" &&
        !siteLogoData.startsWith("http")
      ) {
        let imageFormat = "image/png";
        if (siteLogoData.startsWith("/9j/")) {
          imageFormat = "image/jpeg";
        } else if (siteLogoData.startsWith("iVBORw0KGgo")) {
          imageFormat = "image/png";
        }
        logoDataUrl = `data:${imageFormat};base64,${siteLogoData}`;
      } else if (
        typeof siteLogoData === "string" &&
        siteLogoData.startsWith("http")
      ) {
        logoDataUrl = await loadImageSrc(siteLogoData);
      }

      await new Promise(resolve => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
          try {
            const aspectRatio = img.width / img.height;
            let finalWidth = logoWidth;
            let finalHeight = logoHeight;
            if (aspectRatio > logoWidth / logoHeight) {
              finalHeight = logoWidth / aspectRatio;
            } else {
              finalWidth = logoHeight * aspectRatio;
            }
            const offsetX = (logoWidth - finalWidth) / 2;
            const offsetY = (logoHeight - finalHeight) / 2;

            const canvas = document.createElement("canvas");
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0);
            const dataUrl = canvas.toDataURL("image/png");

            pdf.addImage(
              dataUrl,
              "PNG",
              logoX + offsetX,
              logoY + offsetY,
              finalWidth,
              finalHeight
            );
            resolve();
          } catch (error) {
            resolve();
          }
        };
        img.onerror = () => resolve();
        img.src = logoDataUrl;
      });
    } catch (error) {
      // continue without logo
    }
  }

  pdf.setFontSize(10);
  pdf.setTextColor(0, 0, 0);
  pdf.setFont("helvetica", "bold");
  pdf.text(orgName || "Hospital Name Here", margin + 40, y + 5);
  pdf.setFont("helvetica", "normal");
  pdf.setTextColor(85, 85, 85);
  pdf.text(siteName || "Place here", margin + 40, y + 11);

  // Remidio logo (top right)
  await new Promise(resolve => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.width || 200;
        canvas.height = img.height || 50;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL("image/png");

        const maxHeight = 8;
        const aspectRatio = img.width / img.height;
        const lWidth = maxHeight * aspectRatio;
        const lHeight = maxHeight;
        const lX = margin + contentWidth - lWidth - 5;
        const lY = y + 3;

        pdf.addImage(dataUrl, "PNG", lX, lY, lWidth, lHeight);
        resolve();
      } catch (error) {
        pdf.setFontSize(8);
        pdf.setTextColor(0, 0, 0);
        pdf.setFont("helvetica", "normal");
        pdf.text("Remidio", margin + contentWidth - 20, y + 7);
        resolve();
      }
    };
    img.onerror = () => {
      pdf.setFontSize(8);
      pdf.setTextColor(0, 0, 0);
      pdf.setFont("helvetica", "normal");
      pdf.text("Remidio", margin + contentWidth - 20, y + 7);
      resolve();
    };
    img.src = remidioLogo;
  });
}

function drawGreenHeading(pdf, margin, startY, height, contentWidth) {
  const y = margin + startY;
  pdf.setFillColor(63, 190, 115);
  pdf.rect(margin, y, contentWidth, height, "F");

  pdf.setFontSize(11);
  pdf.setTextColor(255, 255, 255);
  pdf.setFont("helvetica", "bold");
  pdf.text(
    "Retinal Imaging Report - Remidio Insta Z",
    margin + contentWidth / 2,
    y + 3.5,
    { align: "center" }
  );
}

function drawPatientDataLine(
  pdf,
  patient,
  margin,
  startY,
  height,
  contentWidth
) {
  const y = margin + startY + 3;
  const dataItems = [
    { label: "Patient Name :", value: patient.name || "N/A" },
    { label: "Patient ID :", value: patient.patientId || "N/A" },
    {
      label: "Age/ DOB/ Gender:",
      value: `${patient.age || "N/A"} yrs/ ${
        patient.dateOfBirth
          ? patient.dateOfBirth.split("/").length === 3
            ? `${patient.dateOfBirth.split("/")[1]}/${
                patient.dateOfBirth.split("/")[0]
              }/${patient.dateOfBirth.split("/")[2]}`
            : patient.dateOfBirth
          : "N/A"
      }/ ${normalizeGender(patient.gender, "N/A").charAt(0)}`
    },
    {
      label: "Date:",
      value: (() => {
        const today = new Date();
        return `${today
          .getDate()
          .toString()
          .padStart(2, "0")}/${(today.getMonth() + 1)
          .toString()
          .padStart(2, "0")}/${today.getFullYear()}`;
      })()
    }
  ];

  const itemWidth = contentWidth / dataItems.length;

  pdf.setFontSize(8);
  dataItems.forEach((item, index) => {
    const x = margin + index * itemWidth + 5;

    pdf.setTextColor(0, 0, 0);
    pdf.setFont("helvetica", "bold");
    pdf.text(item.label, x, y);

    pdf.setTextColor(85, 85, 85);
    pdf.setFont("helvetica", "normal");
    pdf.text(item.value, x, y + 4);
  });
}

async function drawFooter(
  pdf,
  patient,
  margin,
  startY,
  contentWidth,
  qrCodeLink,
  isFirstPage,
  signatureData = null
) {
  const boxHeight = 18;
  const boxY = startY;

  const qrSize = 18;
  const qrLeftMargin = 5;
  const gapBetweenQrAndBox = 8;

  const qrAreaWidth = qrLeftMargin + qrSize + gapBetweenQrAndBox;
  const boxWidth = contentWidth - qrAreaWidth;
  const boxX = margin + qrAreaWidth;

  pdf.setDrawColor(63, 190, 115);
  pdf.setLineWidth(0.5);
  pdf.roundedRect(boxX, boxY, boxWidth, boxHeight, 2, 2);

  const commentsWidth = boxWidth * 0.75;
  const signatureWidth = boxWidth * 0.25;
  pdf.setDrawColor(63, 190, 115);
  pdf.setLineWidth(0.3);
  pdf.line(boxX + commentsWidth, boxY, boxX + commentsWidth, boxY + boxHeight);

  pdf.setFontSize(9);
  pdf.setTextColor(85, 85, 85);
  pdf.setFont("helvetica", "normal");
  pdf.text("Comments :", boxX + 5, boxY + 7);

  const signatureAreaX = boxX + commentsWidth + 5;
  const signatureAreaWidth = signatureWidth - 10;

  if (signatureData) {
    try {
      const signatureHeight = 10;
      const signatureY = boxY + 2;
      pdf.addImage(
        signatureData,
        "PNG",
        signatureAreaX,
        signatureY,
        signatureAreaWidth,
        signatureHeight,
        undefined,
        "FAST"
      );
    } catch (error) {
      pdf.setDrawColor(0, 0, 0);
      pdf.setLineWidth(0.5);
      pdf.line(
        signatureAreaX,
        boxY + boxHeight - 7,
        boxX + boxWidth - 5,
        boxY + boxHeight - 7
      );
    }
  } else {
    pdf.setDrawColor(0, 0, 0);
    pdf.setLineWidth(0.5);
    pdf.line(
      signatureAreaX,
      boxY + boxHeight - 7,
      boxX + boxWidth - 5,
      boxY + boxHeight - 7
    );
  }

  pdf.setFontSize(9);
  pdf.setTextColor(85, 85, 85);
  pdf.text("Doctor's Signature", signatureAreaX, boxY + boxHeight - 2);

  if (qrCodeLink && qrCodeLink.trim() !== "" && isFirstPage) {
    await drawQRCode(pdf, qrCodeLink, margin + qrLeftMargin, boxY, qrSize);
  }
}

async function drawImage(pdf, imageUrl, x, y, width, height, maskSize = 3) {
  return new Promise(resolve => {
    const img = new Image();
    img.crossOrigin = "anonymous";

    img.onload = () => {
      try {
        pdf.setDrawColor(63, 190, 115);
        pdf.setLineWidth(0.5);
        pdf.roundedRect(x, y, width, height, 2, 2);

        const size = Math.min(width, height) * 0.98;
        const centerX = x + width / 2;
        const centerY = y + height / 2;
        const imgX = centerX - size / 2;
        const imgY = centerY - size / 2;

        const canvasSize = Math.min(img.width, img.height);
        const canvas = document.createElement("canvas");
        canvas.width = canvasSize;
        canvas.height = canvasSize;
        const ctx = canvas.getContext("2d");

        ctx.fillStyle = "#FFFFFF";
        ctx.fillRect(0, 0, canvasSize, canvasSize);

        const maxRadius = canvasSize / 2;
        const DEFAULT_RATIO = 0.9;
        const STEP_RATIO = 0.05;
        const defaultRadius = maxRadius * DEFAULT_RATIO;
        const step = maxRadius * STEP_RATIO;
        let radius;
        if (maskSize <= 2) {
          radius = defaultRadius - maskSize * step;
        } else {
          radius = Math.min(maxRadius, defaultRadius + (maskSize - 2) * step);
        }

        ctx.beginPath();
        ctx.arc(canvasSize / 2, canvasSize / 2, radius, 0, Math.PI * 2);
        ctx.clip();

        const scale = Math.min(canvasSize / img.width, canvasSize / img.height);
        const scaledWidth = img.width * scale;
        const scaledHeight = img.height * scale;
        const drawX = (canvasSize - scaledWidth) / 2;
        const drawY = (canvasSize - scaledHeight) / 2;
        ctx.drawImage(img, drawX, drawY, scaledWidth, scaledHeight);

        const dataUrl = canvas.toDataURL("image/png", 1.0);
        pdf.addImage(dataUrl, "PNG", imgX, imgY, size, size, undefined, "FAST");

        resolve();
      } catch (error) {
        pdf.setFillColor(255, 255, 255);
        pdf.circle(
          x + width / 2,
          y + height / 2,
          Math.min(width, height) / 2,
          "F"
        );
        resolve();
      }
    };

    img.onerror = () => {
      pdf.setFillColor(255, 255, 255);
      pdf.circle(
        x + width / 2,
        y + height / 2,
        Math.min(width, height) / 2,
        "F"
      );
      resolve();
    };

    img.src = imageUrl;
  });
}

/**
 * Replace the dark outer background of a montage image with white, preserving the
 * circular fundus boundary. Ported verbatim from the desktop app.
 */
function floodFillBackgroundToWhite(imageData) {
  const { data, width, height } = imageData;
  const N = width * height;
  const SEED_THRESHOLD = 6;
  const GROW_THRESHOLD = 20;
  const GROW_ITERATIONS = 15;

  const maxCh = new Uint8Array(N);
  for (let i = 0; i < N; i++) {
    const idx = i * 4;
    maxCh[i] = Math.max(data[idx], data[idx + 1], data[idx + 2]);
  }

  const outerBg = new Uint8Array(N);

  const fill = (startX, startY) => {
    const k0 = startY * width + startX;
    if (outerBg[k0] || maxCh[k0] >= SEED_THRESHOLD) return;

    const stack = [startX, startY];
    while (stack.length > 0) {
      const sy = stack.pop();
      const sx = stack.pop();

      let left = sx;
      while (
        left > 0 &&
        !outerBg[sy * width + left - 1] &&
        maxCh[sy * width + left - 1] < SEED_THRESHOLD
      )
        left--;
      let right = sx;
      while (
        right < width - 1 &&
        !outerBg[sy * width + right + 1] &&
        maxCh[sy * width + right + 1] < SEED_THRESHOLD
      )
        right++;

      let prevAbove = false;
      let prevBelow = false;
      for (let cx = left; cx <= right; cx++) {
        const k = sy * width + cx;
        if (outerBg[k] || maxCh[k] >= SEED_THRESHOLD) {
          prevAbove = false;
          prevBelow = false;
          continue;
        }
        outerBg[k] = 1;

        if (sy > 0) {
          const ak = (sy - 1) * width + cx;
          const ad = !outerBg[ak] && maxCh[ak] < SEED_THRESHOLD;
          if (ad && !prevAbove) stack.push(cx, sy - 1);
          prevAbove = ad;
        }
        if (sy < height - 1) {
          const bk = (sy + 1) * width + cx;
          const bd = !outerBg[bk] && maxCh[bk] < SEED_THRESHOLD;
          if (bd && !prevBelow) stack.push(cx, sy + 1);
          prevBelow = bd;
        }
      }
    }
  };

  fill(0, 0);
  fill(width - 1, 0);
  fill(0, height - 1);
  fill(width - 1, height - 1);
  const edgeStep = Math.max(1, Math.floor(Math.min(width, height) / 20));
  for (let ex = 0; ex < width; ex += edgeStep) {
    fill(ex, 0);
    fill(ex, height - 1);
  }
  for (let ey = 0; ey < height; ey += edgeStep) {
    fill(0, ey);
    fill(width - 1, ey);
  }

  for (let iter = 0; iter < GROW_ITERATIONS; iter++) {
    const frontier = [];
    for (let yy = 0; yy < height; yy++) {
      for (let xx = 0; xx < width; xx++) {
        const k = yy * width + xx;
        if (outerBg[k]) continue;
        if (maxCh[k] >= GROW_THRESHOLD) continue;
        if (
          (xx > 0 && outerBg[k - 1]) ||
          (xx < width - 1 && outerBg[k + 1]) ||
          (yy > 0 && outerBg[k - width]) ||
          (yy < height - 1 && outerBg[k + width])
        ) {
          frontier.push(k);
        }
      }
    }
    if (frontier.length === 0) break;
    for (const k of frontier) outerBg[k] = 1;
  }

  const DS = 4;
  const dw = Math.ceil(width / DS);
  const dh = Math.ceil(height / DS);
  const dsMask = new Float32Array(dw * dh);

  for (let dy = 0; dy < dh; dy++) {
    for (let dx = 0; dx < dw; dx++) {
      let sum = 0;
      let cnt = 0;
      for (let sy = dy * DS; sy < Math.min((dy + 1) * DS, height); sy++) {
        for (let sx = dx * DS; sx < Math.min((dx + 1) * DS, width); sx++) {
          sum += outerBg[sy * width + sx];
          cnt++;
        }
      }
      dsMask[dy * dw + dx] = sum / cnt;
    }
  }

  const dsTmp = new Float32Array(dw * dh);
  for (let pass = 0; pass < 3; pass++) {
    for (let yy = 0; yy < dh; yy++) {
      const off = yy * dw;
      for (let xx = 0; xx < dw; xx++) {
        let sum = 0;
        let cnt = 0;
        for (
          let kx = Math.max(0, xx - 2);
          kx <= Math.min(dw - 1, xx + 2);
          kx++
        ) {
          sum += dsMask[off + kx];
          cnt++;
        }
        dsTmp[off + xx] = sum / cnt;
      }
    }
    for (let xx = 0; xx < dw; xx++) {
      for (let yy = 0; yy < dh; yy++) {
        let sum = 0;
        let cnt = 0;
        for (
          let ky = Math.max(0, yy - 2);
          ky <= Math.min(dh - 1, yy + 2);
          ky++
        ) {
          sum += dsTmp[ky * dw + xx];
          cnt++;
        }
        dsMask[yy * dw + xx] = sum / cnt;
      }
    }
  }

  const mask = new Float32Array(N);
  for (let yy = 0; yy < height; yy++) {
    const fy = (yy + 0.5) / DS - 0.5;
    const y0 = Math.max(0, Math.floor(fy));
    const y1 = Math.min(dh - 1, y0 + 1);
    const wy = fy - y0;
    for (let xx = 0; xx < width; xx++) {
      const fx = (xx + 0.5) / DS - 0.5;
      const x0 = Math.max(0, Math.floor(fx));
      const x1 = Math.min(dw - 1, x0 + 1);
      const wx = fx - x0;
      mask[yy * width + xx] =
        dsMask[y0 * dw + x0] * (1 - wx) * (1 - wy) +
        dsMask[y0 * dw + x1] * wx * (1 - wy) +
        dsMask[y1 * dw + x0] * (1 - wx) * wy +
        dsMask[y1 * dw + x1] * wx * wy;
    }
  }

  for (let i = 0; i < N; i++) {
    if (mask[i] > 0.5) {
      const idx = i * 4;
      data[idx] = 255;
      data[idx + 1] = 255;
      data[idx + 2] = 255;
    }
  }
}

async function drawMontageImage(pdf, imageUrl, x, y, width, height) {
  return new Promise(resolve => {
    const img = new Image();
    img.crossOrigin = "anonymous";

    img.onload = () => {
      try {
        pdf.setDrawColor(63, 190, 115);
        pdf.setLineWidth(0.5);
        pdf.roundedRect(x, y, width, height, 2, 2);

        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");

        ctx.fillStyle = "#FFFFFF";
        ctx.fillRect(0, 0, img.width, img.height);
        ctx.drawImage(img, 0, 0, img.width, img.height);

        const imageData = ctx.getImageData(0, 0, img.width, img.height);
        floodFillBackgroundToWhite(imageData);
        ctx.putImageData(imageData, 0, 0);

        const dataUrl = canvas.toDataURL("image/png", 1.0);

        const imgAspectRatio = img.width / img.height;
        const cellAspectRatio = width / height;

        let drawWidth;
        let drawHeight;
        let drawXPdf;
        let drawYPdf;

        if (imgAspectRatio > cellAspectRatio) {
          drawWidth = width * 0.98;
          drawHeight = drawWidth / imgAspectRatio;
          drawXPdf = x + (width - drawWidth) / 2;
          drawYPdf = y + (height - drawHeight) / 2;
        } else {
          drawHeight = height * 0.98;
          drawWidth = drawHeight * imgAspectRatio;
          drawXPdf = x + (width - drawWidth) / 2;
          drawYPdf = y + (height - drawHeight) / 2;
        }

        pdf.addImage(
          dataUrl,
          "PNG",
          drawXPdf,
          drawYPdf,
          drawWidth,
          drawHeight,
          undefined,
          "FAST"
        );

        resolve();
      } catch (error) {
        pdf.setFillColor(255, 255, 255);
        pdf.rect(x, y, width, height, "F");
        resolve();
      }
    };

    img.onerror = () => {
      pdf.setFillColor(255, 255, 255);
      pdf.rect(x, y, width, height, "F");
      resolve();
    };

    img.src = imageUrl;
  });
}

async function drawQRCode(pdf, qrCodeLink, x, y, size) {
  try {
    const typeNumber = 4;
    const errorCorrectionLevel = "H";
    const qr = qrcode(typeNumber, errorCorrectionLevel);
    qr.addData(qrCodeLink);
    qr.make();

    const canvas = document.createElement("canvas");
    const cellSize = 4;
    const qrMargin = 4;
    const qrSize = qr.getModuleCount() * cellSize;
    canvas.width = qrSize + qrMargin * 2;
    canvas.height = qrSize + qrMargin * 2;
    const ctx = canvas.getContext("2d");

    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.fillStyle = "#3FBE73";
    for (let row = 0; row < qr.getModuleCount(); row++) {
      for (let col = 0; col < qr.getModuleCount(); col++) {
        if (qr.isDark(row, col)) {
          ctx.fillRect(
            qrMargin + col * cellSize,
            qrMargin + row * cellSize,
            cellSize,
            cellSize
          );
        }
      }
    }

    const qrDataUrl = canvas.toDataURL("image/png");
    pdf.addImage(qrDataUrl, "PNG", x, y, size, size);

    pdf.setFontSize(8);
    pdf.setTextColor(93, 183, 116);
    pdf.setFont("helvetica", "italic");
    pdf.text("Scan to know more", x, y + size + 3);
    pdf.text("about your eye conditions", x, y + size + 6);
  } catch (error) {
    // If QR code generation fails, skip it.
  }
}
